"use client"

import { useState, useCallback, useRef, useEffect } from "react"
import { Check, CheckCircle, X } from "lucide-react"

type ColorVariant = "green" | "red" | "blue" | "purple" | "gray"

interface SlideToConfirmProps {
  /** スライド完了時のコールバック */
  onComplete: () => void
  /** 逆操作時のコールバック（右→左スライドでOFF） */
  onReverse?: () => void
  /** 処理中フラグ */
  isProcessing?: boolean
  /** 確認完了済みフラグ */
  isCompleted?: boolean
  /** 無効化フラグ */
  disabled?: boolean
  /** デフォルトのラベルテキスト */
  label?: string
  /** 処理中のラベルテキスト */
  processingLabel?: string
  /** 完了時のラベルテキスト */
  completedLabel?: string
  /** 逆操作時のラベルテキスト */
  reverseLabel?: string
  /** カラーバリアント */
  colorVariant?: ColorVariant
  /** コンパクトサイズ（h-10）か通常サイズ（h-14）か */
  compact?: boolean
  /** テスト用識別子（data-testid に設定される） */
  dataTestId?: string
}

const colorConfig: Record<ColorVariant, {
  background: string
  button: string
  buttonHover: string
  text: string
  disabledBackground: string
  disabledButton: string
}> = {
  green: {
    background: "bg-gradient-to-r from-green-100 to-green-50",
    button: "bg-green-500",
    buttonHover: "hover:bg-green-600",
    text: "text-green-800",
    disabledBackground: "bg-gradient-to-r from-gray-100 to-gray-50",
    disabledButton: "bg-gray-400",
  },
  red: {
    background: "bg-gradient-to-r from-red-100 to-red-50",
    button: "bg-red-500",
    buttonHover: "hover:bg-red-600",
    text: "text-red-800",
    disabledBackground: "bg-gradient-to-r from-gray-100 to-gray-50",
    disabledButton: "bg-gray-400",
  },
  blue: {
    background: "bg-gradient-to-r from-blue-100 to-blue-50",
    button: "bg-blue-500",
    buttonHover: "hover:bg-blue-600",
    text: "text-blue-800",
    disabledBackground: "bg-gradient-to-r from-gray-100 to-gray-50",
    disabledButton: "bg-gray-400",
  },
  purple: {
    background: "bg-gradient-to-r from-purple-100 to-purple-50",
    button: "bg-purple-500",
    buttonHover: "hover:bg-purple-600",
    text: "text-purple-800",
    disabledBackground: "bg-gradient-to-r from-gray-100 to-gray-50",
    disabledButton: "bg-gray-400",
  },
  gray: {
    background: "bg-gradient-to-r from-gray-100 to-gray-50",
    button: "bg-gray-400",
    buttonHover: "hover:bg-gray-500",
    text: "text-gray-600",
    disabledBackground: "bg-gradient-to-r from-gray-100 to-gray-50",
    disabledButton: "bg-gray-400",
  },
}

export function SlideToConfirm({
  onComplete,
  onReverse,
  isProcessing = false,
  isCompleted = false,
  disabled = false,
  label = "スライドして確認",
  processingLabel = "確認処理中...",
  completedLabel = "確認完了",
  reverseLabel = "スライドして取消",
  colorVariant = "green",
  compact = false,
  dataTestId,
}: SlideToConfirmProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [dragPosition, setDragPosition] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const dynamicMaxRef = useRef(0)

  // コールバックをrefに保持（useEffect内でのstale closure対策）
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete
  const onReverseRef = useRef(onReverse)
  onReverseRef.current = onReverse

  const config = colorConfig[colorVariant]

  // 逆操作モード判定（完了済み＋onReverseあり＋処理中でない＋無効でない）
  const isReverseMode = !!(isCompleted && onReverse && !isProcessing && !disabled)

  // 元のisDisabledロジック維持（逆操作モードでは有効にする）
  const isDisabled = disabled || isCompleted || isProcessing
  const isInteractionDisabled = isDisabled && !isReverseMode

  // コンパクトサイズ用のしきい値とボタンサイズ
  const threshold = compact ? 150 : 200
  const maxDrag = compact ? 180 : 250
  const buttonSize = compact ? "w-8 h-8" : "w-12 h-12"
  const iconSize = compact ? "w-4 h-4" : "w-6 h-6"
  const containerHeight = compact ? "h-10" : "h-14"
  const textMargin = isReverseMode ? "" : (compact ? "ml-8" : "ml-12")
  const textSize = compact ? "text-sm" : ""

  // コンテナの動的最大ドラッグ距離を計算
  const calcDynamicMax = useCallback(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.getBoundingClientRect().width
      const buttonWidth = compact ? 32 : 48
      const padding = 8
      return containerWidth - buttonWidth - padding
    }
    return maxDrag
  }, [compact, maxDrag])

  // ドラッグ開始ハンドラ（ボタンのmousedown/touchstartに紐付け）
  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    if (isReverseMode) {
      const dynMax = calcDynamicMax()
      dynamicMaxRef.current = dynMax
      setDragPosition(dynMax)
      setIsDragging(true)
    } else if (!isDisabled) {
      setIsDragging(true)
    }
  }, [isReverseMode, isDisabled, calcDynamicMax])

  // document-levelイベントリスナーでドラッグ追跡（コンテナ外でも動作）
  useEffect(() => {
    if (!isDragging || !containerRef.current) return

    const container = containerRef.current
    let triggered = false

    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (triggered) return

      const rect = container.getBoundingClientRect()
      let clientX: number
      if ("touches" in e) {
        // #64: スマホでスライド中にページスクロールへジェスチャーを奪われないよう抑止する。
        // touchmove は passive:false で登録しているため preventDefault が有効。
        if (e.cancelable) e.preventDefault()
        clientX = e.touches[0].clientX
      } else {
        clientX = e.clientX
      }

      const currentMax = isReverseMode ? (dynamicMaxRef.current || maxDrag) : maxDrag
      const buttonOffset = compact ? 20 : 25
      const newPosition = Math.max(0, Math.min(currentMax, clientX - rect.left - buttonOffset))
      setDragPosition(newPosition)

      // しきい値チェック（ドラッグ中にリアルタイムで判定）
      if (isReverseMode) {
        const dynMax = dynamicMaxRef.current || maxDrag
        if ((dynMax - newPosition) > threshold) {
          triggered = true
          setIsDragging(false)
          setDragPosition(0)
          onReverseRef.current?.()
        }
      } else {
        if (newPosition > threshold) {
          triggered = true
          setIsDragging(false)
          setDragPosition(0)
          onCompleteRef.current()
        }
      }
    }

    const handleEnd = () => {
      if (!triggered) {
        setDragPosition(0)
      }
      setIsDragging(false)
    }

    document.addEventListener("mousemove", handleMove)
    document.addEventListener("mouseup", handleEnd)
    // #64: passive:false にして handleMove 内の preventDefault を有効化（スマホのスクロール奪取を防止）
    document.addEventListener("touchmove", handleMove, { passive: false })
    document.addEventListener("touchend", handleEnd)

    return () => {
      document.removeEventListener("mousemove", handleMove)
      document.removeEventListener("mouseup", handleEnd)
      document.removeEventListener("touchmove", handleMove)
      document.removeEventListener("touchend", handleEnd)
    }
  }, [isDragging, isReverseMode, compact, maxDrag, threshold])

  // ラベルテキストを決定
  const displayLabel = isProcessing
    ? processingLabel
    : isReverseMode
      ? reverseLabel
      : isCompleted
        ? completedLabel
        : label

  // ボタンのスタイルを決定
  const getButtonStyle = (): React.CSSProperties => {
    if (isDragging) {
      return { transform: `translateX(${dragPosition}px)` }
    }
    if (isCompleted) {
      const endPos = calcDynamicMax()
      return { transform: `translateX(${endPos}px)` }
    }
    return { transform: `translateX(${dragPosition}px)` }
  }

  return (
    <div className="relative">
      <div
        ref={containerRef}
        data-testid={dataTestId || "slide-to-confirm"}
        className={`w-full ${containerHeight} ${
          isInteractionDisabled ? config.disabledBackground : config.background
        } rounded-full relative overflow-hidden cursor-pointer select-none p-1 touch-none`}
      >
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className={`font-medium ${textMargin} ${textSize} ${
            isInteractionDisabled ? "text-gray-500" : config.text
          }`}>
            {displayLabel}
          </span>
        </div>
        <div
          className={`absolute left-1 top-1 ${buttonSize} ${
            isInteractionDisabled ? config.disabledButton : config.button
          } rounded-full flex items-center justify-center ${
            isInteractionDisabled ? "cursor-not-allowed" : "cursor-grab active:cursor-grabbing"
          } ${isDragging ? "" : "transition-all duration-200"} touch-none`}
          style={getButtonStyle()}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          {isReverseMode ? (
            <X className={`${iconSize} text-white`} />
          ) : isCompleted ? (
            <CheckCircle className={`${iconSize} text-white`} />
          ) : (
            <Check className={`${iconSize} text-white`} />
          )}
        </div>
      </div>
    </div>
  )
}
